/**
 * Sidebar Cmd/Ctrl+K keyboard-shortcut hook. The dependency array is
 * `[isCollapsed, isMobile]`; `setIsCollapsed` and `searchInputRef` are stable
 * identities threaded in as arguments.
 */

import { useEffect } from 'react';
import type { RefObject } from 'react';

interface UseSidebarKeyboardShortcutsArgs {
  isMobile: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
}

export function useSidebarKeyboardShortcuts({
  isMobile,
  isCollapsed,
  setIsCollapsed,
  searchInputRef,
}: UseSidebarKeyboardShortcutsArgs): void {
  useEffect(() => {
    if (isMobile) return;

    const handleSidebarSearchShortcut = (event: KeyboardEvent) => {
      const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isShortcut) return;

      event.preventDefault();
      if (isCollapsed) {
        setIsCollapsed(false);
      }
      setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    window.addEventListener('keydown', handleSidebarSearchShortcut);
    return () => window.removeEventListener('keydown', handleSidebarSearchShortcut);
  }, [isCollapsed, isMobile]);
}
